// ============================================================
// Plum Town — bilingual dictionary (JP / EN)
// Used by client components via useLang().
// ============================================================

export type Lang = "jp" | "en";

export type Dict = (typeof DICT)["jp"];

export const DICT = {
  jp: {
    shopButton: "オンラインショップ",
    scroll: "SCROLL",
    nav: {
      brand: "Brand",
      agriculture: "Agriculture",
      news: "News",
      company: "Company",
      contact: "Contact",
    },
    hero: {
      subtitle: "地域に、誇りを",
    },
    vision: {
      label: "Vision",
      title: "西湘地域の価値を世界へ",
      paragraphs: [
        "西湘地域には、長い年月をかけて育まれた美しい風景と、\n人の手で守り継がれてきた確かな技があり、海と山が交わるこのまちには、\nものづくり、食の豊かさ、文化や歴史が息づいていて、\n私たちは、そのひとつひとつに宿る「地域らしさ」を信じています。",
        "ただの“ローカル”ではなく、世界が憧れる価値へと磨き上げたい。それは、古きを誇りに、新しきを恐れず挑むこと。",
        "西湘地域の価値を、世界の価値へ。\nこのまちの可能性を信じ、未来へつなぐ——\nそれが、私たちの使命であり、情熱です。",
      ],
    },
    brand: {
      label: "Brand",
      title: 'THE HEALING LIQUEUR「SOGA」',
      cta: "オンラインショップへ",
    },
    partners: {
      label: "Shop List",
      title: "取り扱い店舗",
      siteLabel: "サイト",
      categoryLabels: {
        "ホテル・旅館": "ホテル・旅館",
        "飲食店": "飲食店",
        "小売店": "小売店",
      } as Record<string, string>,
      note: "※ 掲載しているのは取引先の一部です。許諾をいただいた店舗のみを公開しています。",
      footlineBefore: "取り扱い・提携のご相談は",
      footlineLink: "こちら",
      footlineAfter: "からお問い合わせください。",
    },
    agripreview: {
      label: "Agriculture",
      title: "農業事業",
      body: "2026年新たに梅林事業への挑戦を始めました。\n小田原が約600年以上もの歴史を誇る、梅林を未来へ繋なぐ為に、持続的な梅林運営を目指していきます。",
      stats: [] as Array<{ num: string; unit: string; label: string }>,
      cta: "農業事業の詳細を見る",
      placeholder: "[ 梅林 ]",
    },
    news: {
      label: "News",
      viewMore: "View More",
      noImage: "[ no image ]",
    },
    company: {
      label: "Company",
      subtitle: "会社情報",
      missionLabel: "Vision",
      mission: "西湘地域の価値を世界へ",
      foundersLabel: "Founders",
      info: [
        ["会社名", "Plum Town株式会社"],
        ["設立", "2025年6月"],
        ["資本金", "500万円"],
        ["所在地", "神奈川県小田原市"],
        ["事業内容", "クラフト梅酒「SOGA」の企画・販売／梅林の運営／地域素材のブランド開発"],
      ] as Array<[string, string]>,
      team: [
        { name: "善波 弘志", role: "代表取締役 / Co-Founder" },
        { name: "小泉 慎太郎", role: "取締役 / Co-Founder" },
      ],
    },
    footer: {
      address: "神奈川県小田原市",
      copyright: "© Plum Town Inc.",
      privacy: "プライバシーポリシー",
    },
    agriculturePage: {
      eyebrow: "Agriculture",
      title: "梅から、育てる。",
      sub: "小田原・十郎梅の自社栽培プロジェクト",
      whyTitle: "農業への挑戦",
      whyBody:
        "小田原が誇る曽我梅林は関東3大梅林とも称され、約600年の歴史があります。しかし現在は農家の高齢化や後継ぎ不足の影響もあり、年々耕作放棄地も増加しています。\n私達は小田原の梅の歴史を未来に繋ぐ為、持続可能な新たな農業モデルを模索していきます。",
      issues: [
        {
          title: "農家の減少と後継ぎ不足",
          body: "農家の高齢化や後継ぎ不足により、年々梅の収穫量は減少傾向にあります。",
        },
        {
          title: "十郎梅のブランド化",
          body: "小田原独自の品種であり、その味わいはまるで桃のような香りと柑橘を思わせる爽やかさを兼ね備えた品種です。より高付加価値なブランド梅としての地位を確立していくことを目指します。",
        },
        {
          title: "6次化",
          body: "より高付加価値で6次化を実現する為に梅酒ブランドを自社開発し、世界の人を魅了する酒として展開していきます。",
        },
      ],
      approachTitle: "私達の取り組み",
      steps: [
        {
          title: "梅林の運営受託",
          body: "梅林の運営を受託し、耕作放棄地にせず梅の文化と歴史を未来に繋ぎます。",
        },
        {
          title: "梅守隊(コミュニティ)",
          body: "梅林の運営をコミュニティ化し、地域の人と共に農作業を楽しみながら梅林と関わる機会を提供します。",
        },
        {
          title: "梅本来の価値を、再定義する",
          body: "梅酒の原料に留めない。生食、菓子、化粧品、ギフト、観光体験——梅本来の価値を引き出す活動を、ここから始めます。",
        },
      ],
      futureLabel: "Future",
      futureTitle: "梅文化を未来へ",
      futureBody:
        "梅は小田原で約600年も前から人々の生活に寄り添ってきました。この歴史と文化を未来へ繋ぐだけでなく、地域の誇りとして更に進化させていくことがPlumTownの目指す世界です。\n梅の街、小田原として世界中の人を魅了する梅林となるようこれからも挑戦を続けていきます。",
      futureCta: "農業事業について問い合わせる",
      panoramicPlaceholder: "[ 梅林 ]",
      stepPlaceholder: (i: number) => `[ 取り組み ${i + 1} ]`,
    },
    contactPage: {
      label: "Contact",
      title: "お問い合わせ",
      lede:
        "事業のご相談、取材依頼、提携、農業や地域連携について。\nまずはお気軽にお声がけください。",
      inquiryHeading: "ご用件の例",
      inquiryLabel: "Inquiry types",
      inquiryTypes: [
        "事業のご相談",
        "提携・取り扱い",
        "取材・メディア",
        "農業・地域連携",
        "その他",
      ],
      otherLabel: "そのほかの窓口",
      otherShop: "オンラインショップ",
      otherNote: "note",
      otherInstagram: "Instagram",
      form: {
        label: "Contact form",
        heading: "フォームからのお問い合わせ",
        name: "お名前",
        namePlaceholder: "山田 太郎",
        company: "会社名・団体名",
        companyPlaceholder: "株式会社サンプル",
        email: "メールアドレス",
        emailPlaceholder: "example@email.com",
        phone: "電話番号",
        phonePlaceholder: "03-1234-5678",
        type: "ご用件",
        typeOptions: {
          product: "商品について",
          wholesale: "提携・取り扱い",
          media: "取材・メディア",
          recruit: "採用",
          other: "その他",
        },
        message: "お問い合わせ内容",
        messagePlaceholder: "ご相談内容をご記入ください",
        optional: "任意",
        privacyNotePrefix: "ご入力いただいた個人情報は、",
        privacyNoteLink: "プライバシーポリシー",
        privacyNoteSuffix: "に従って取り扱います。",
        submit: "送信する",
        submitting: "送信中…",
        successTitle: "送信が完了しました",
        successBody:
          "お問い合わせありがとうございます。\n内容を確認のうえ、担当者よりご連絡いたします。",
        errors: {
          name: "お名前を入力してください",
          email: "メールアドレスを入力してください",
          emailFormat: "メールアドレスの形式をご確認ください",
          message: "お問い合わせ内容を入力してください",
          rateLimited:
            "送信が集中しています。しばらく時間をおいてから再度お試しください。",
          turnstile: "確認に失敗しました。ページを再読み込みしてお試しください。",
          failed:
            "送信に失敗しました。お手数ですが時間をおいて再度お試しください。",
        },
      },
    },
  },
  en: {
    shopButton: "Online Shop",
    scroll: "SCROLL",
    nav: {
      brand: "Brand",
      agriculture: "Agriculture",
      news: "News",
      company: "Company",
      contact: "Contact",
    },
    hero: {
      subtitle: "Pride, born of this place.",
    },
    vision: {
      label: "Vision",
      title: "Local value, made global.",
      paragraphs: [
        "The Seisho region holds landscapes shaped over centuries\nand crafts kept alive by patient hands.\nWhere mountains meet the sea, you find food, craft, history, culture —\nwe believe in the 'local-ness' that lives in every detail.",
        "Not just 'local.' We want to refine it into something the world envies — proud of the old, unafraid of the new.",
        "From the Seisho region to the world.\nWe believe in this town and carry it into its future —\nthat is our mission, and our passion.",
      ],
    },
    brand: {
      label: "Brand",
      title: 'THE HEALING LIQUEUR "SOGA"',
      cta: "Visit Online Shop",
    },
    partners: {
      label: "Shop List",
      title: "Where SOGA is served",
      siteLabel: "Site",
      categoryLabels: {
        "ホテル・旅館": "Hotel & Ryokan",
        "飲食店": "Dining",
        "小売店": "Retail",
      } as Record<string, string>,
      note: "* This list shows only a selection of our partners — those who have agreed to be featured here.",
      footlineBefore: "For distribution and partnership inquiries,",
      footlineLink: "contact us",
      footlineAfter: ".",
    },
    agripreview: {
      label: "Agriculture",
      title: "Our farming business",
      body:
        "In 2026, we took on a new challenge: stewarding a plum grove.\nOdawara holds over 600 years of plum heritage — we aim to carry it forward through sustainable grove operations.",
      stats: [] as Array<{ num: string; unit: string; label: string }>,
      cta: "Read about our farming",
      placeholder: "[ Plum grove ]",
    },
    news: {
      label: "News",
      viewMore: "View More",
      noImage: "[ no image ]",
    },
    company: {
      label: "Company",
      subtitle: "Company Info",
      missionLabel: "Vision",
      mission: "Carry the value of the Seisho region into the world.",
      foundersLabel: "Founders",
      info: [
        ["Name", "Plum Town Inc."],
        ["Founded", "June 2025"],
        ["Capital", "JPY 5,000,000"],
        ["Location", "Odawara, Kanagawa, Japan"],
        [
          "Business",
          'Craft plum liqueur "SOGA" / Plum grove operations / Regional ingredient branding',
        ],
      ] as Array<[string, string]>,
      team: [
        { name: "Hiroshi Zemba", role: "CEO / Co-Founder" },
        { name: "Shintaro Koizumi", role: "Director / Co-Founder" },
      ],
    },
    footer: {
      address: "Odawara, Kanagawa, Japan",
      copyright: "© Plum Town Inc.",
      privacy: "Privacy Policy",
    },
    agriculturePage: {
      eyebrow: "Agriculture",
      title: "From the plum, we grow.",
      sub: "In-house cultivation of Juro-ume in Odawara",
      whyTitle: "Taking on farming.",
      whyBody:
        "Soga Plum Grove — one of the Kanto region's three great groves — has carried 600 years of plum heritage in Odawara. Today, ageing farmers and a lack of successors are leaving more land abandoned each year.\nTo carry this plum heritage into the future, we are seeking a new, sustainable model of farming.",
      issues: [
        {
          title: "Fewer farmers, fewer successors",
          body:
            "With ageing farmers and a shortage of successors, plum harvests are shrinking year by year.",
        },
        {
          title: "Branding Juro-ume",
          body:
            "A cultivar unique to Odawara, with a peach-like fragrance and a citrus-bright freshness. We aim to establish Juro-ume as a premium brand plum.",
        },
        {
          title: "Sixth-sector integration",
          body:
            "To realize sixth-sector value, we develop our own plum liqueur brand and offer it as a spirit that captivates audiences worldwide.",
        },
      ],
      approachTitle: "Our initiatives",
      steps: [
        {
          title: "Stewarding plum groves",
          body:
            "We take on the operation of plum groves, keeping them from being abandoned and carrying their culture and history forward.",
        },
        {
          title: "Umemori-tai (community)",
          body:
            "We open grove operations to the community, sharing the joys of farm work and creating ways for locals to take part in the grove.",
        },
        {
          title: "Redefining the value of plum",
          body:
            "Not just liqueur. Fresh fruit, sweets, cosmetics, gifts, tourism — many ways to lift this fruit's worth.",
        },
      ],
      futureLabel: "Toward the future",
      futureTitle: "Plum culture, into the future.",
      futureBody:
        "Plums have been part of life in Odawara for over 600 years. Carrying this heritage forward — and evolving it into a source of regional pride — is the future Plum Town is reaching for.\nWe will keep taking on the challenge of making Odawara, a town of plums, a grove that captivates people from around the world.",
      futureCta: "Inquire about farming",
      panoramicPlaceholder: "[ Plum grove ]",
      stepPlaceholder: (i: number) => `[ Initiative ${i + 1} ]`,
    },
    contactPage: {
      label: "Contact",
      title: "Get in touch",
      lede:
        "Business inquiries, press, partnerships, farming and community collaboration.\nReach out — we read everything.",
      inquiryHeading: "Inquiry types",
      inquiryLabel: "Inquiry types",
      inquiryTypes: [
        "Business inquiry",
        "Distribution / Partnership",
        "Press / Media",
        "Farming / Community",
        "Other",
      ],
      otherLabel: "Other channels",
      otherShop: "Online Shop",
      otherNote: "note",
      otherInstagram: "Instagram",
      form: {
        label: "Contact form",
        heading: "Send us a message",
        name: "Name",
        namePlaceholder: "Taro Yamada",
        company: "Company / Organization",
        companyPlaceholder: "Sample Inc.",
        email: "Email",
        emailPlaceholder: "example@email.com",
        phone: "Phone",
        phonePlaceholder: "+81 3-1234-5678",
        type: "Inquiry type",
        typeOptions: {
          product: "About our products",
          wholesale: "Distribution / Partnership",
          media: "Press / Media",
          recruit: "Careers",
          other: "Other",
        },
        message: "Message",
        messagePlaceholder: "How can we help?",
        optional: "optional",
        privacyNotePrefix: "We handle the information you provide in accordance with our ",
        privacyNoteLink: "Privacy Policy",
        privacyNoteSuffix: " (Japanese).",
        submit: "Send",
        submitting: "Sending…",
        successTitle: "Message sent",
        successBody:
          "Thank you for reaching out.\nWe will review your message and get back to you.",
        errors: {
          name: "Please enter your name",
          email: "Please enter your email address",
          emailFormat: "Please check the email format",
          message: "Please enter your message",
          rateLimited: "Too many requests. Please try again in a moment.",
          turnstile: "Verification failed. Please reload the page and try again.",
          failed: "Failed to send. Please try again in a few moments.",
        },
      },
    },
  },
} as const;

export function getDict(lang: Lang): Dict {
  return DICT[lang] as Dict;
}
